/**
 * Planning an issue to a run (the keeper's task): which lots at the run's site can be handed to
 * it, and the request body. Pure.
 */
import type { StockRow } from '../../api/store'

export interface IssueRun {
  id: string
  status: string
  siteId: string | null
  cropId: string | null
  stream: string
  originUnitId: string | null
}

/** Runs that can still take bags. */
export function canPlanIssue(run: Pick<IssueRun, 'status'>): boolean {
  return run.status === 'OPEN' || run.status === 'RUNNING'
}

export interface IssueLot {
  unitId: string
  unitCode: string
  unitType: string
  farmerCode: string | null
  bags: number
  kgNowG: number
  places: string[]
}

/**
 * Stock rows → lots that may go to this run: at the run's site, in a place (not on a trip), not on
 * hold, of the run's crop; a brand run only takes brand lots. One entry per lot (rows are per
 * place), the run's own origin lot first.
 */
export function issueCandidates(rows: readonly StockRow[], run: IssueRun): IssueLot[] {
  const by = new Map<string, IssueLot>()
  for (const r of rows) {
    if (r.placeKind !== 'LOCATION' || r.siteId !== run.siteId) continue
    if (r.unitHold !== 'NONE' || r.bags - r.bagsOnHold <= 0) continue
    if (run.cropId && r.cropId && r.cropId !== run.cropId) continue
    if (run.stream === 'MAIN' && r.stream !== 'MAIN') continue
    if (!['LOT', 'ORPHAN', 'BATCH'].includes(r.unitType)) continue
    const cur = by.get(r.unitId)
    if (cur) {
      cur.bags += r.bags - r.bagsOnHold
      cur.kgNowG += r.kgNowG
      if (r.locationCode && !cur.places.includes(r.locationCode)) cur.places.push(r.locationCode)
    } else {
      by.set(r.unitId, {
        unitId: r.unitId,
        unitCode: r.unitCode,
        unitType: r.unitType,
        farmerCode: r.farmerCode,
        bags: r.bags - r.bagsOnHold,
        kgNowG: r.kgNowG,
        places: r.locationCode ? [r.locationCode] : [],
      })
    }
  }
  return [...by.values()].sort((a, b) =>
    a.unitId === run.originUnitId ? -1 : b.unitId === run.originUnitId ? 1 : a.unitCode.localeCompare(b.unitCode),
  )
}

/** POST /shipments body for an issue task. */
export function issueRequest(runId: string, unitIds: readonly string[]) {
  return { kind: 'ISSUE' as const, runId, unitIds: [...unitIds] }
}
